import { shouldEnsureGuestSession } from '../auth/guestOrgMismatch';

/**
 * startGuestRun — shared guest run-creation call for the J2 start-here slot
 * and the J4 commitment sheet (#1921).
 *
 * Deliberately the SAME request shape `HomePageV2.jsx`'s existing
 * `handleScenarioClick` already uses for its guest-facing scenario cards
 * (`POST /api/runs`, guest login-if-needed, navigate to `/chat/:runId`) —
 * this is not a new run-creation path, just the existing one reused with a
 * caller-chosen `modality` instead of a hardcoded `'text'`.
 *
 * Dependencies are passed in rather than imported so this stays a plain,
 * jsdom-free-testable function (`reference_frontend_no_jsdom` — no `../main`
 * import chain, no hook calls) — the two call sites (`GuestDiscoveryContextsPage`,
 * `GuestDiscoveryShortlistPage`) supply their own `safeAuthFetch`/`navigate`/
 * `loginAsGuest`/`isAuthenticated` from their own hook calls.
 *
 * @param {object} params
 * @param {number} params.scenarioId
 * @param {'text'|'voice'} params.modality
 * @param {boolean} params.isAuthenticated
 * @param {object|null} [params.user] - the AuthContext user; #3753: a guest
 *   (user.is_guest) also goes through loginAsGuest, which keeps a same-org
 *   guest and mints a fresh one for a guest from another org.
 * @param {(opts?: {scenarioId?: number}) => Promise<void>} params.loginAsGuest
 * @param {(url: string, init?: object) => Promise<Response>} params.safeAuthFetch
 * @param {string} params.apiBaseUrl
 * @param {(path: string, opts?: object) => void} params.navigate
 * @returns {Promise<void>}
 */
export async function startGuestRun({
  scenarioId,
  modality,
  isAuthenticated,
  user = null,
  loginAsGuest,
  safeAuthFetch,
  apiBaseUrl,
  navigate,
}) {
  if (shouldEnsureGuestSession({ isAuthenticated, user })) {
    // #3753: judged (and minted) by the scenario being started, not the page.
    await loginAsGuest({ scenarioId });
  }

  const response = await safeAuthFetch(`${apiBaseUrl}/api/runs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scenarioId, modality }),
  });
  const data = await response.json();
  const runId = data.run?.id || data.id;
  if (!runId) throw new Error(data.error || 'Failed to start session');

  // #1921 follow-up: seed `preferredMode` so ChatPage honours the modality
  // CommitmentSheet's row 1 already decided (its own comment: "row 1 here
  // IS the type/voice decision") — same location.state contract
  // FocusPracticeModal's runs use (ChatPage.jsx ~L486/~L942) and the same
  // "one gate, no replay" intent ScenarioDetailPage's `skipYomaBriefing`
  // hint documents for #1837. Without this, the navigate is URL-only and
  // ChatPage falls back to its own mode-select gate, silently discarding
  // the choice the guest just made.
  navigate(`/chat/${runId}?demo=true`, { state: { preferredMode: modality } });
}
